import { useContext, useEffect, useMemo, useState } from 'react';

import {
	Button,
	Flex,
	Form,
	Input,
	Modal,
	Tooltip,
	Typography,
	message,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import TextArea from 'antd/es/input/TextArea';
import Link from 'antd/es/typography/Link';
import { Tabs } from 'antd/lib';

import {
	useCreateCreatorsCollectionAndAddCreatorIntoIt,
	useGetCreatorByUrl,
	useGetCreatorsCollectionsWithIds,
} from '@/api/monitor_collections';
import { useDebounce } from '@/hooks';
import prepareImageSrc from '@/lib/helpers/prepareImageSrc';
import AddRemoveCreatorsCollectionsTable from '@/pages/app/dashboard/video-insights-co-pilot/collections/Creators/AddRemoveCreatorsCollectionsTable';
// import { useCollectionStore } from "@store";
import { addRemoveCollectionsColumns } from '@/pages/app/dashboard/video-insights-co-pilot/collections/Videos/data';
import { CollectionContext } from '@/providers/CollectionsContextProvider';
import { IPagination, TFullData } from '@/ts';

import styles from './styles.module.css';

import placeholderImage from '../../lib/assets/placeholder.jpg';

export default function AddCreatorToCollectionModal() {
	const {
		isAddCreatorToCollectionModalOpen,
		setIsAddCreatorToCollectionModalOpen,
		modalData,
	} = useContext(CollectionContext);

	const [form] = useForm();

	const [sort, setSort] = useState<'created_at' | 'status'>('created_at');
	const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
	const [tabsActiveKey, setTabsActiveKey] = useState('1');
	const [search, setSearch] = useState('');

	const debouncedSearch = useDebounce(search);

	const [pagination, setPagination] = useState<IPagination>({
		page: 1,
		pageCount: 1,
		total: 0,
		limit: 10,
	});

	const platformHandle =
		modalData?.creator?.platformHandles?.[1] ||
		modalData?.creator?.platformHandles?.[0];

	const {
		data: searchCreatorData,
		isLoading: isSearchCreatorLoading,
		isFetching: isSearchCreatorFetching,
		isError: isSearchCreatorError,
		isSuccess: isSearchCreatorSuccess,
		error: searchError,
	} = useGetCreatorByUrl(platformHandle);

	// const [tags, setTags] = useState<SelectProps["options"]>(
	//   modalData?.tags?.map((keyword: string) => ({
	//     label: keyword,
	//     value: keyword,
	//   })) || []
	// );

	// const {
	//   data: videoIdData,
	//   isLoading: isVideoIdDataLoading,
	//   isFetching: isVideoIdDataFetching,
	//   isError: isVideoIdDataError,
	//   error: videoIdError,
	// } = useGetVideoIdByURL({
	//   url: modalData?.url,
	//   enabled: isAddCreatorToCollectionModalOpen && !!modalData?.url,
	// });

	const {
		data: collectionsData,
		isLoading,
		isFetching,
	} = useGetCreatorsCollectionsWithIds({
		pagination,
		sortBy: sort,
		sortOrder,
		url: modalData?.url,
		enabled: isAddCreatorToCollectionModalOpen && !!modalData?.url,
		search: debouncedSearch.length >= 3 ? debouncedSearch : '',
	});

	const {
		mutate: addCreatorsCollectionWithCreator,
		isLoading: isAddCollectionLoading,
	} = useCreateCreatorsCollectionAndAddCreatorIntoIt();

	// const { data: tagsData, isFetching: isTagsFetching } = useGetCreatorsTags({
	//   enabled: isAddCreatorToCollectionModalOpen,
	// });

	const creatorData: TFullData['creator'] =
		modalData?.creator as unknown as any;
	const platformData = Object.values(creatorData?.platforms ?? {})?.[0];

	const items = collectionsData?.data?.data?.items || [];
	const total = collectionsData?.data?.data?.pagination?.total || 0;

	const creator = {
		creator_mongo_id: searchCreatorData?.data?._id,
		url: platformHandle,
		tags: [],
	};

	useEffect(() => {
		if (isSearchCreatorSuccess) {
			const mongoId = searchCreatorData?.data?._id;
			if (!mongoId) {
				message.warning(
					"We couldn't find this creator in our database. Please try again later.",
					5,
				);
			}
		}
		if (isSearchCreatorError) {
			const err = searchError as any;
			message.error(
				`An error occured: ${err?.response?.data?.message || err?.message}.`,
			);
			console.error('error', searchError);
		}
	}, [isSearchCreatorSuccess, isSearchCreatorError]);

	const onTableChange = (__: any, _: any, sorter: any) => {
		setSort(sorter.field ?? 'created_at');
		setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
	};

	const handleOk = async () => {
		try {
			await form.validateFields();

			const { title, description } = form.getFieldsValue();

			addCreatorsCollectionWithCreator(
				{
					title,
					description,
					creator_mongo_id: creator?.creator_mongo_id,
					url: creator?.url,
					tags: creator?.tags || [],
				},
				{
					onSuccess: () => {
						message.success('Added');
						setIsAddCreatorToCollectionModalOpen(false);
						form.resetFields();
						setTabsActiveKey('1');
					},
					onError: (error) => {
						const err = error as any;
						message.error(
							`An error occured: ${
								err?.response?.data?.message || err?.message
							}.`,
						);
						console.error('error', error);
					},
				},
			);
		} catch (error) {
			console.error('Error adding collection:', error);
			message.error('Please fill all required fields.');
		}
	};

	const handleCancel = () => {
		setPagination((cur) => ({ ...cur, page: 1 }));
		form.resetFields();
		setIsAddCreatorToCollectionModalOpen(false);
		setTabsActiveKey('1');
	};

	// const handleChange = (value: string[]) => {
	//   setTags(value.map((item) => ({ label: item, value: item })));
	// };

	// const tagsItems = tagsData?.data?.data?.items?.map((item: any) => ({
	//   label: item?.tag,
	//   value: item?.tag,
	// }));

	const columns = useMemo(() => addRemoveCollectionsColumns('creators'), []);

	return (
		<Modal
			title="Add creator to collection"
			// centered
			width={800}
			open={isAddCreatorToCollectionModalOpen}
			onOk={handleOk}
			onCancel={handleCancel}
			closable
			// loading={isFetching || isLoading}
			maskClosable={false}
			footer={[
				<Button
					key="back"
					size="small"
					onClick={handleCancel}
					disabled={isAddCollectionLoading}
				>
					Close
				</Button>,
				tabsActiveKey === '2' && (
					<Button
						key="submit"
						size="small"
						type="primary"
						loading={isAddCollectionLoading}
						onClick={handleOk}
						disabled={isAddCollectionLoading || !creator?.creator_mongo_id}
					>
						Add
					</Button>
				),
			]}
		>
			<Flex
				vertical
				justify="center"
				align="center"
				style={{ marginBottom: '1rem' }}
				gap={8}
			>
				<Flex gap={8} align="center">
					{modalData?.creator?.profileImageURL ? (
						<img
							src={
								modalData?.creator?.profileImageURL?.startsWith('http')
									? modalData?.creator?.profileImageURL?.replace(
											'dev1.',
											'dev.',
									  )
									: prepareImageSrc(modalData?.creator?.profileImageURL)
							}
							alt={modalData?.creator?.profileName}
							className={styles.creatorThumbnail}
							width={100}
							height={100}
							onError={(e) => {
								// @ts-ignore
								e.target.src = placeholderImage; // Fallback image
							}}
						/>
					) : (
						<img
							src={placeholderImage}
							width={100}
							height={100}
							className={styles.creatorThumbnail}
							alt="Image not available"
						/>
					)}

					{/* <Flex vertical gap={4} style={{ width: "100%" }}> */}
					{/* <Typography.Text>
            <b>Name:</b> {data?.creator?.profileName}
          </Typography.Text> */}

					{platformData?.url ? (
						<Tooltip title={modalData?.creator?.profileName}>
							<Button
								type="link"
								size="large"
								href={platformData?.url}
								target="_blank"
								style={{
									width: 'fit-content',
									fontSize: '18px',
									display: 'flex',
									wordWrap: 'break-word',
									overflow: 'hidden',
									padding: '2px',
								}}
							>
								<span
									style={{
										width: '100%',
										fontSize: '18px',
										display: 'inline-block',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
									}}
								>
									{modalData?.creator?.profileName}
								</span>
							</Button>
						</Tooltip>
					) : (
						<Tooltip title={modalData?.creator?.profileName}>
							<Flex vertical>
								<Typography.Text
									style={{
										width: 'fit-content',
										fontSize: '18px',
										display: 'inline-block',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
									}}
								>
									{platformData?.username}
								</Typography.Text>
							</Flex>
						</Tooltip>
					)}
					{/* </Flex> */}
				</Flex>
				<Link
					href={
						modalData?.creator?.platformHandles?.[1] ||
						modalData?.creator?.platformHandles?.[0]
					}
				>
					{modalData?.creator?.platformHandles?.[1] ||
						modalData?.creator?.platformHandles?.[0]}
				</Link>
			</Flex>
			<Tabs
				defaultActiveKey="1"
				size="small"
				style={{ width: '100%' }}
				activeKey={tabsActiveKey}
				onChange={setTabsActiveKey}
				items={[
					{
						key: '1',
						label: 'Existing Collections',
						children: (
							<Flex vertical gap={12}>
								<Typography.Title level={5} style={{ margin: 0 }}>
									Add/remove to/from existing collections
								</Typography.Title>
								<Input
									type="text"
									placeholder="Search..."
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									size="small"
								/>
								<AddRemoveCreatorsCollectionsTable
									// key={id}
									data={items}
									columns={columns}
									onTableChange={onTableChange}
									isLoading={isLoading}
									isFetching={isFetching}
									pagination={{ ...pagination, total }}
									setPagination={setPagination}
									creator={creator as any}
								/>
							</Flex>
						),
					},
					{
						key: '2',
						label: 'New Collection',
						children: (
							<>
								<Typography.Title level={5} style={{ margin: '0 0 0.5rem' }}>
									Create new collection with this creator
								</Typography.Title>
								<Form
									form={form}
									layout="vertical"
									initialValues={{
										title: '',
										description: '',
									}}
									style={{ marginBottom: '1rem' }}
								>
									<Flex vertical gap={16}>
										<Form.Item
											name="title"
											label="Title"
											// layout="vertical"
											rules={[
												{
													required: true,
													message: 'Please provide collection name',
												},
												{
													whitespace: true,
													message: 'Collection title cannot be empty.',
												},
											]}
											style={{ margin: 0 }}
										>
											<Input
												placeholder="Collection name"
												style={{ resize: 'none' }}
											/>
										</Form.Item>

										<Form.Item
											label="Description"
											name="description"
											style={{ marginBottom: '1rem' }}
										>
											<TextArea
												placeholder={'Collection description'}
												style={{ resize: 'none' }}
												rows={3}
												maxLength={255}
												count={{
													show: true,
													max: 255,
												}}
											/>
										</Form.Item>

										{/* <Form.Item name="tags" style={{ margin: 0 }}>
            <Select
              mode="tags"
              style={{ width: "100%" }}
              placeholder="Search tags"
              onChange={handleChange}
              options={tagsItems || []}
              // disabled={isUpdateCreatorLoading}
              // @ts-ignore
              // defaultValue={keywords || []}
            />
          </Form.Item> */}
									</Flex>
								</Form>
							</>
						),
					},
				]}
			></Tabs>
		</Modal>
	);
}
